import { faCar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useRef, useState } from 'react';
import Button from 'components/ui/Button/Button';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CreateProposalResponse, Position } from 'ts/hotel/interfaces/Generic';
import { createMapDirection } from 'services/hotel';
import PdfPrintOrSend from '../../Result/Modals/PdfPrintOrSend';
import ReactDOM from 'react-dom/client';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import useDraggableModal from 'hooks/useDraggableModal';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useTranslation } from 'react-i18next';
import { getKey } from 'utils/hotel';
import { MainContext } from 'contexts/MainContext';
import { imgWaypointA, imgWaypointB } from 'constants/generic';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface ReportModalOSMProps {
  onClose: () => void;
  directions: any;
}
const ReportModalOSM: FC<ReportModalOSMProps> = ({ onClose, directions }) => {
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [directionResponse, setDirectionResponse] = useState<CreateProposalResponse>();
  const { hotelIndex } = useContext(HotelConfigContext);
  const {
    modalRef,
    modalStyle,
    dragHandleProps,
    isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  const onClickOverlay = (event: any) => {
    if (event.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };

  const onClickPrint = async () => {
    setLoadingPrint(true);

    const span = document.createElement('span');
    const root = ReactDOM.createRoot(span);

    root.render(
      <html>
      <head>
        <meta charSet="UTF-8" />
      </head>
      <body>
        <div>
          <Content driving={directions.driving} title="Driving" />
          <Content driving={directions.walking} title="Walking" />
        </div>
      </body>
    </html>
    );

    setTimeout(async () => {
      const params = {
        searchToken: sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex)) || '',
        html: btoa(span.outerHTML),
      };
      const response = await createMapDirection(params);
      setDirectionResponse(response);

      setLoadingPrint(false);
    },
    200);
  };

  return (
    <div ref={modalOverlayRef} className="modal-overlay modal-maximizable" onClick={onClickOverlay}>
      <div ref={modalRef} className="modal active" style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <div className="title text-lg">Map route</div>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content">
          {!!directions.driving && <Content driving={directions.driving} title={'Driving'} />}
          {!!directions.walking && <Content driving={directions.walking} title={'Walking'} />}
        </div>
        <div className="modal-footer">
          <Button
            title="Create Map Route"
            className={'btn btn-primary ml-0' + (!directionResponse ? ' mr-a' : '')}
            onClick={onClickPrint}
            disabled={loadingPrint}
          />
          <div className="mr-a">
            <PdfPrintOrSend
              show={!!directionResponse}
              email={directionResponse?.email}
              pdfUrl={directionResponse?.pdfUrl}
            />
          </div>
          <Button title="Close" className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(ReportModalOSM, 'ReportModalOSM');

interface ContentProps {
  driving: any;
  title: string;
}
const Content: FC<ContentProps> = ({ driving, title }) => {
  return (
    <Fragment>
      <Ways ways={driving} icon={faCar} title={title} />
    </Fragment>
  );
};

interface WaysProps {
  ways: {
    instructions: any[];
    summary: any;
    from: Position;
    to: Position;
  };
  icon: IconProp;
  title: string;
}
const Ways: FC<WaysProps> = ({ ways, icon, title }) => {
  const { initSiteData } = useContext(MainContext);

  const { t } = useTranslation();

  return (
    <div style={{ lineHeight: 1.5, padding: '0 10px' }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 10 }}>
        <FontAwesomeIcon icon={icon} style={{ width: 16 }} /> {title}
        <span>
          {ways.summary.totalDistance ? ' ' + Math.floor(ways.summary.totalDistance) + 'm' : ''}
        </span>{' '}
        /
        <span>
          {ways.summary.totalTime ? ' ' + Math.floor(ways.summary.totalTime / 60) + 'min' : ''}
        </span>
      </div>
      <div style={{ margin: '10px 0' }}>
        <img style={{ marginRight: 10 }} src={imgWaypointA} alt="A" />
        <span>{ways.from.address}</span>
      </div>
      {ways.instructions.map((step: any, index: number) => (
        <div style={{ display: 'flex', justifyContent: 'space-between' }} key={index}>
          <div>
            <span style={{ marginRight: 10 }}>{index + 1}.</span>
            <span>
              {t(step.modifier, { distance: step.distance ? Math.floor(step.distance) + 'm' : '' })}
              {step.modifier ? ' ' : ''}
              {t(step.type, {
                distance: step.distance ? Math.floor(step.distance) + 'm' : '',
                name: step.name,
              })}
            </span>
          </div>
          <div>{step.distance ? Math.floor(step.distance) + 'm' : ''}</div>
        </div>
      ))}
      <div style={{ margin: '10px 0' }}>
        <img style={{ marginRight: 10 }} src={imgWaypointB} alt="B" />
        <span>{ways.to.address}</span>
      </div>
      <div style={{ marginBottom: 5 }}>
        <img
          style={{ width: 550, height: 200 }}
          src={`https://maps.googleapis.com/maps/api/staticmap?key=${initSiteData?.apiKey || ''}&size=550x200&markers=color:green%7Clabel:A%7C${ways.from.lat},${ways.from.lng}&markers=color:red%7Clabel:B%7C${ways.to.lat},${ways.to.lng}&path=weight:5`}
          alt=""
        />
      </div>

      <a
        style={{ color: '#666', textDecoration: 'underline' }}
        href={`https://www.google.com/maps/dir/${ways.from.lat},${ways.from.lng}/${ways.to.lat},${ways.to.lng}`}
        target="_blank"
        rel="noreferrer"
      >
        Show directions on Google
      </a>
    </div>
  );
};
